import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "./CalendarPanel.css";
import axios from "axios";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";

const obtenerMesActual = () => {
  const ahora = new Date();
  return `${ahora.getFullYear()}-${(ahora.getMonth() + 1)
    .toString()
    .padStart(2, "0")}`;
};

// Primer día del mes "AAAA-MM" en hora local. No usar new Date("AAAA-MM-01"):
// esa forma se lee como medianoche UTC y al oeste de Greenwich cae en el mes
// anterior (en Latinoamérica el calendario abría siempre un mes atrás).
const inicioDeMes = (mes) => new Date(Number(mes.slice(0, 4)), Number(mes.slice(5, 7)) - 1, 1);

export default function CalendarPanel({ onContactar }) {
  const [monthPlan, setMonthPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);
  const [userId, setUserId] = useState(null);

  const mesActual = obtenerMesActual();

  // Cargar el userId desde Capacitor Storage (asíncrono)
  useEffect(() => {
    async function loadUserId() {
      const { user } = await getSession();
      setUserId(user?._id || user?.id || null);
    }
    loadUserId();
  }, []);

  // Fetch de plan mensual
  useEffect(() => {
    if (!userId) {
      setError("No hay usuario logueado. Por favor, inicia sesión de nuevo.");
      return;
    }
    async function fetchPlan() {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(
          `${API_URL}/month-plan?userId=${userId}&mes=${mesActual}`
        );
        setMonthPlan(res.data);
      } catch (err) {
        try {
          const res = await axios.post(
            `${API_URL}/month-plan/generate`,
            { userId, mes: mesActual }
          );
          setMonthPlan(res.data);
        } catch (err2) {
          setError("No se pudo generar ni cargar tu calendario mensual.");
        }
      } finally {
        setLoading(false);
      }
    }
    fetchPlan();
  }, [userId, mesActual]);

  // Buscar info del día.
  // La cuadrícula del calendario enseña días del mes anterior y del siguiente;
  // si se busca sólo por número de día, esos heredan los datos de este mes.
  const getDayData = (date) => {
    if (!monthPlan?.dias) return null;
    // `mesActual` es "AAAA-MM": sólo hay datos para los días de ese mes
    const [anio, mes] = String(mesActual).split("-").map(Number);
    if (date.getFullYear() !== anio || date.getMonth() + 1 !== mes) return null;
    return monthPlan.dias.find((d) => d.fecha === `Dia ${date.getDate()}`);
  };

  // Renderiza íconos por día en el calendario
  const tileContent = ({ date, view }) => {
    if (view !== "month") return null;
    const dayData = getDayData(date);
    if (!dayData) return null;
    return dayData.rutinas?.length > 0 ? <span className="cal-punto" aria-hidden="true" /> : null;
  };

  // Al seleccionar un día
  const onDayClick = (date) => {
    const dayData = getDayData(date);
    setSelectedDay(
      dayData
        ? { ...dayData, rawDate: date }
        : {
            fecha: `Dia ${date.getDate()}`,
            comidas: [],
            rutinas: [],
            ejercicios: [],
            rawDate: date,
          }
    );
  };

  const nombreDe = (x, porDefecto) =>
    typeof x === "object" ? x.nombre || x.name || porDefecto : String(x);

  return (
    <div className="cal-panel">
      {loading && <div className="app-estado">Cargando tu calendario mensual...</div>}
      {error && <div className="app-estado app-estado-error">{error}</div>}

      {!loading && !error && monthPlan && (
        <Calendar
          locale="es-ES"
          tileContent={tileContent}
          onClickDay={onDayClick}
          value={new Date()}
          minDetail="month"
          maxDetail="month"
          activeStartDate={inicioDeMes(mesActual)}
          tileClassName={({ date, view }) => {
            if (view === "month" && getDayData(date)) {
              return "day-with-data";
            }
            return null;
          }}
        />
      )}
      <p className="cal-leyenda">
        <span className="cal-punto" /> Día con entrenamiento. Toca un día para ver el detalle.
      </p>

      {selectedDay && (
        <div className="popup-overlay" onClick={() => setSelectedDay(null)}>
          <div className="popup cal-dia" onClick={(e) => e.stopPropagation()}>
            <button
              className="close-button"
              onClick={() => setSelectedDay(null)}
              aria-label="Cerrar"
            >
              ✕
            </button>
            <h2>
              {selectedDay.rawDate
                ? selectedDay.rawDate.toLocaleDateString("es-ES", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })
                : selectedDay.fecha}
            </h2>

            {selectedDay.rutinas?.length > 0 && (
              <section className="cal-grupo">
                <h3>Entrenamiento</h3>
                <ul>
                  {selectedDay.rutinas.map((rutina, idx) => (
                    <li key={idx}>{nombreDe(rutina, "Rutina")}</li>
                  ))}
                </ul>
              </section>
            )}

            {selectedDay.ejercicios?.length > 0 && (
              <section className="cal-grupo">
                <h3>Ejercicios</h3>
                <ul>
                  {selectedDay.ejercicios.map((ej, idx) => (
                    <li key={idx}>
                      <span>{nombreDe(ej, "Ejercicio")}</span>
                      {typeof ej === "object" && (ej.caloriesBurned || ej.tipo) && (
                        <span className="cal-detalle">
                          {[ej.tipo, ej.caloriesBurned && `${ej.caloriesBurned} kcal`]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {selectedDay.comidas?.length > 0 && (
              <section className="cal-grupo">
                <h3>Comidas</h3>
                <ul>
                  {selectedDay.comidas.map((comida, idx) => (
                    <li key={idx}>
                      <span>{nombreDe(comida, "Comida")}</span>
                      {typeof comida === "object" && comida.calorias && (
                        <span className="cal-detalle">{comida.calorias} kcal</span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {!selectedDay.comidas?.length &&
              !selectedDay.rutinas?.length &&
              !selectedDay.ejercicios?.length && (
                <div className="app-estado">No hay nada planificado este día.</div>
              )}

            {/* Pedir cita: por el chat con el entrenador, sin servicios externos */}
            <button className="app-btn" onClick={onContactar}>
              Pedir cita a tu entrenador
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
